import {
  TravelTripTypes,
  type TripFormValue,
} from '../../components/TravelTripForm/types'
import { type TravelAddress } from '../../entities/TravelAddress'
import { type TravelDestination } from '../../entities/TravelDestination'
import {
  type UpdateTravelDestinationAddressPayload,
  type UpdateTravelDestinationPayload,
} from '../api-models/UpdateTravelDestinationPayload'

// `PlaceResult.reference` is deprecated upstream (absent from the typings)
// but Angular forwards it and the backend persists it; fall back to
// `place_id` (its documented replacement).
const getPlaceReference = (place: google.maps.places.PlaceResult): string => {
  const { reference } = place as { reference?: string }
  return reference ?? place.place_id ?? ''
}

// Port of Angular's `formatPostLocation` for a NEWLY selected place,
// echoing the existing persisted address id so the backend updates the
// address record in place.
const formatPlaceAddress = (
  place: google.maps.places.PlaceResult,
  existingAddressId: number
): UpdateTravelDestinationAddressPayload => {
  const components = place.address_components ?? []
  const formattedAddress = place.formatted_address ?? place.name ?? ''

  return {
    id: existingAddressId,
    addressLineOne: formattedAddress,
    city: components[0]?.long_name || components[0]?.short_name || ' ',
    state_province: components[components.length - 2]?.short_name ?? ' ',
    country: components[components.length - 1]?.long_name ?? ' ',
    name: formattedAddress,
    postalCode: 'N/A',
  }
}

// Unchanged place — echo back the persisted address verbatim (ids +
// `postalCode`, often the literal "N/A").
const echoExistingAddress = (
  address: TravelAddress
): UpdateTravelDestinationAddressPayload => ({
  id: address.id,
  name: address.name,
  addressLineOne: address.addressLineOne,
  city: address.city ?? ' ',
  state_province: address.stateProvince ?? ' ',
  country: address.country ?? ' ',
  postalCode: address.postalCode,
})

interface PlaceLegPayload {
  placeId: string
  address: UpdateTravelDestinationAddressPayload
  reference: string
}

// A leg either keeps its persisted place (echo everything back verbatim) or
// carries a newly selected PlaceResult (rebuild the address, echoing the
// persisted address id so the backend updates the record in place).
const mapPlaceLeg = (
  place: google.maps.places.PlaceResult,
  existingPlaceId: string,
  existingReference: string,
  existingAddress: TravelAddress
): PlaceLegPayload => {
  const isChanged = !!place.place_id && place.place_id !== existingPlaceId

  if (!isChanged) {
    return {
      placeId: existingPlaceId,
      address: echoExistingAddress(existingAddress),
      reference: existingReference,
    }
  }

  return {
    placeId: place.place_id ?? existingPlaceId,
    address: formatPlaceAddress(place, existingAddress.id),
    reference: getPlaceReference(place),
  }
}

/**
 * Maps a confirmed trip-form value + the destination being edited to the
 * `PUT /api/v3/travels/destinations/{id}/` payload. Dates are ISO datetimes
 * with offset; one-way trips save arrival date = departure date (BHV-017).
 */
export const mapEditTripFormToPayload = (
  trip: TripFormValue,
  destination: TravelDestination
): UpdateTravelDestinationPayload => {
  const departureDate = trip.departureDate.format()

  // BHV-017: one-way trips normalize the arrival date to the departure date.
  const arrivalDate =
    trip.tripType === TravelTripTypes.RETURN_TRIP && trip.returnDate
      ? trip.returnDate.format()
      : departureDate

  const departureLeg = mapPlaceLeg(
    trip.departurePlace,
    destination.departurePlaceId,
    destination.departureReference,
    destination.departureAddress
  )
  const arrivalLeg = mapPlaceLeg(
    trip.arrivalPlace,
    destination.arrivalPlaceId,
    destination.arrivalReference,
    destination.arrivalAddress
  )

  return {
    sequence: destination.sequence,
    Transport: trip.transport,
    departure: departureLeg.placeId,
    departureDate,
    departureAddress: departureLeg.address,
    departureReference: departureLeg.reference,
    arrival: arrivalLeg.placeId,
    arrivalDate,
    arrivalAddress: arrivalLeg.address,
    arrivalReference: arrivalLeg.reference,
  }
}
